import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';

import {
  fetchMetricsSummary,
  fetchTimeSeries,
  fetchUserBreakdown,
} from 'api/aiClipperMetrics/client';
import {
  AiClipperMetricsFilter,
  AiClipperMetricsSummary,
  AiClipperTimeSeriesGranularity,
  AiClipperTimeSeriesResult,
  AiClipperUserBreakdownRow,
} from 'api/aiClipperMetrics/types';

import {
  ATHLETE_DEBOUNCE_MS,
  DEFAULT_PAGE_SIZE,
  DateRange,
  EMPTY_SUMMARY,
  SLUG_LIKE_REGEX,
  runLoad,
  useDebouncedValue,
} from './helpers';

export interface ClipperMetricsErrorMessages {
  summary: string;
  userBreakdown: string;
  series: string;
}

export interface UseClipperMetricsDashboardOptions {
  /** Page-specific filter fields (source types, statuses…). Must be memoised by the caller so the data effects don't loop. */
  extraFilter: AiClipperMetricsFilter;
  errorMessages: ClipperMetricsErrorMessages;
  /** Called by handleClear so the page can reset its own filter state. */
  onClear?: () => void;
}

export interface UseClipperMetricsDashboardResult {
  athleteInput: string;
  range: DateRange;
  setRange: Dispatch<SetStateAction<DateRange>>;
  granularity: AiClipperTimeSeriesGranularity;
  setGranularity: Dispatch<SetStateAction<AiClipperTimeSeriesGranularity>>;
  filter: AiClipperMetricsFilter;
  /** True when the shared athlete/date filters are set; pages OR in their own filters. */
  baseFiltersDirty: boolean;
  summary: AiClipperMetricsSummary;
  summaryLoading: boolean;
  userRows: AiClipperUserBreakdownRow[];
  userTotal: number;
  userTableLoading: boolean;
  userPage: number;
  setUserPage: Dispatch<SetStateAction<number>>;
  userPageSize: number;
  setUserPageSize: Dispatch<SetStateAction<number>>;
  seriesResult: AiClipperTimeSeriesResult;
  seriesLoading: boolean;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  handleRefresh: () => void;
  handleClear: () => void;
  handleAthleteChange: (e: ChangeEvent<HTMLInputElement> | null) => void;
}

export function useClipperMetricsDashboard({
  extraFilter,
  errorMessages,
  onClear,
}: UseClipperMetricsDashboardOptions): UseClipperMetricsDashboardResult {
  const apolloClient = useApolloClient();

  const [athleteInput, setAthleteInput] = useState('');
  const [range, setRange] = useState<DateRange>(null);
  const [granularity, setGranularity] =
    useState<AiClipperTimeSeriesGranularity>('Day');

  const debouncedAthlete = useDebouncedValue(athleteInput, ATHLETE_DEBOUNCE_MS);

  // The only thing that drives the data effects. Day boundaries keep a picked
  // calendar date from shifting across timezones.
  const filter = useMemo<AiClipperMetricsFilter>(() => {
    const next: AiClipperMetricsFilter = { ...extraFilter };
    const athlete = debouncedAthlete.trim();
    if (athlete) {
      if (SLUG_LIKE_REGEX.test(athlete)) next.athleteSlug = athlete;
      else next.athleteName = athlete;
    }
    const fromDate = range?.[0];
    if (fromDate) {
      next.fromDate = fromDate.clone().startOf('day').toISOString();
    }
    const toDate = range?.[1];
    if (toDate) {
      next.toDate = toDate.clone().endOf('day').toISOString();
    }
    return next;
  }, [debouncedAthlete, range, extraFilter]);

  const [summary, setSummary] =
    useState<AiClipperMetricsSummary>(EMPTY_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [userRows, setUserRows] = useState<AiClipperUserBreakdownRow[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userTableLoading, setUserTableLoading] = useState(true);
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [seriesResult, setSeriesResult] = useState<AiClipperTimeSeriesResult>({
    points: [],
    truncated: false,
  });
  const [seriesLoading, setSeriesLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  // Reset page when filter changes (breakdown table only — chart isn't paginated).
  useEffect(() => {
    setUserPage(1);
  }, [filter]);

  const loadSummary = useCallback(
    (filterArg: AiClipperMetricsFilter) =>
      runLoad(setSummaryLoading, setError, errorMessages.summary, async () => {
        const data = await fetchMetricsSummary(apolloClient, filterArg);
        setSummary(data);
      }),
    [apolloClient, errorMessages.summary]
  );

  const loadUserBreakdown = useCallback(
    (filterArg: AiClipperMetricsFilter, p: number, size: number) =>
      runLoad(
        setUserTableLoading,
        setError,
        errorMessages.userBreakdown,
        async () => {
          const data = await fetchUserBreakdown(apolloClient, {
            filter: filterArg,
            limit: size,
            offset: (p - 1) * size,
          });
          setUserRows(data.entities);
          setUserTotal(data.total);
        }
      ),
    [apolloClient, errorMessages.userBreakdown]
  );

  const loadSeries = useCallback(
    (filterArg: AiClipperMetricsFilter, g: AiClipperTimeSeriesGranularity) =>
      runLoad(setSeriesLoading, setError, errorMessages.series, async () => {
        const data = await fetchTimeSeries(apolloClient, {
          filter: filterArg,
          granularity: g,
        });
        setSeriesResult(data);
      }),
    [apolloClient, errorMessages.series]
  );

  useEffect(() => {
    setError(null);
    loadSummary(filter);
  }, [filter, loadSummary]);

  useEffect(() => {
    setError(null);
    loadUserBreakdown(filter, userPage, userPageSize);
  }, [filter, userPage, userPageSize, loadUserBreakdown]);

  useEffect(() => {
    setError(null);
    loadSeries(filter, granularity);
  }, [filter, granularity, loadSeries]);

  const handleRefresh = useCallback(() => {
    setError(null);
    loadSummary(filter);
    loadUserBreakdown(filter, userPage, userPageSize);
    loadSeries(filter, granularity);
  }, [
    filter,
    userPage,
    userPageSize,
    granularity,
    loadSummary,
    loadUserBreakdown,
    loadSeries,
  ]);

  const handleClear = useCallback(() => {
    setAthleteInput('');
    setRange(null);
    onClear?.();
  }, [onClear]);

  const handleAthleteChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> | null) => {
      setAthleteInput(e?.target?.value ?? '');
    },
    []
  );

  const baseFiltersDirty =
    athleteInput.length > 0 || !!range?.[0] || !!range?.[1];

  return {
    athleteInput,
    range,
    setRange,
    granularity,
    setGranularity,
    filter,
    baseFiltersDirty,
    summary,
    summaryLoading,
    userRows,
    userTotal,
    userTableLoading,
    userPage,
    setUserPage,
    userPageSize,
    setUserPageSize,
    seriesResult,
    seriesLoading,
    error,
    setError,
    handleRefresh,
    handleClear,
    handleAthleteChange,
  };
}
